import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Loader2, Lock, CheckCircle2, AlertCircle, Clock, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface DashboardAccessGateProps {
  dashboardType: 'consultant' | 'sdr';
  children: React.ReactNode;
}

export const DashboardAccessGate: React.FC<DashboardAccessGateProps> = ({
  dashboardType,
  children,
}) => {
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [code, setCode] = useState('');
  const [codeRequested, setCodeRequested] = useState(false);
  const [lastError, setLastError] = useState<{ message: string; isRetryable: boolean } | null>(null);

  useEffect(() => {
    checkExistingToken();
  }, []);

  const checkExistingToken = () => {
    const token = localStorage.getItem(`dashboard_access_token_${dashboardType}`);
    if (token) {
      // Simple check - could be enhanced with JWT validation
      setHasAccess(true);
    }
    setLoading(false);
  };

  const handleRequestCode = async () => {
    setRequesting(true);
    setLastError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-dashboard-code', {
        body: { dashboard_type: dashboardType },
      });

      if (error) {
        throw error;
      }

      if (data.success) {
        setCodeRequested(true);
        toast.success('Access code generated!', {
          description: 'If you are logged in as lcosta or njunior, check the bell icon 🔔 in the CRM to see your code.'
        });
      } else {
        const errorMsg = data.error || 'Failed to request code';
        const isRetryable = data.isRetryable || false;
        
        setLastError({ message: errorMsg, isRetryable });
        
        if (isRetryable) {
          toast.error('Connection issue', {
            description: errorMsg + ' Click retry below.'
          });
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (error: any) {
      console.error('Error requesting code:', error);
      
      const errorMessage = error?.message || 'Failed to request access code';
      const isConnectionError = errorMessage.includes('Failed to fetch') || 
                               errorMessage.includes('network') ||
                               errorMessage.includes('timeout');
      
      setLastError({
        message: isConnectionError 
          ? 'Connection problem. The server might be temporarily unavailable.'
          : errorMessage,
        isRetryable: isConnectionError
      });
      
      toast.error(isConnectionError ? 'Connection issue' : 'Error', {
        description: isConnectionError 
          ? 'Server temporarily unavailable. Please try again.'
          : errorMessage
      });
    } finally {
      setRequesting(false);
    }
  };

  const handleValidateCode = async () => {
    if (!code || code.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setValidating(true);
    setLastError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('verify-dashboard-code', {
        body: {
          code,
          dashboard_type: dashboardType,
        },
      });

      if (error) {
        throw error;
      }

      if (data.success && data.token) {
        localStorage.setItem(`dashboard_access_token_${dashboardType}`, data.token);
        setHasAccess(true);
        toast.success('Access granted!');
      } else {
        const errorMsg = data.error || 'Invalid code';
        const isRetryable = data.isRetryable || false;
        
        setLastError({ message: errorMsg, isRetryable });
        toast.error(errorMsg);
      }
    } catch (error: any) {
      console.error('Error validating code:', error);
      
      const errorMessage = error?.message || 'Failed to validate code';
      const isConnectionError = errorMessage.includes('Failed to fetch') || 
                               errorMessage.includes('network') ||
                               errorMessage.includes('timeout');
      
      setLastError({
        message: isConnectionError 
          ? 'Connection problem. The server might be temporarily unavailable.'
          : errorMessage,
        isRetryable: isConnectionError
      });
      
      toast.error(isConnectionError ? 'Connection issue' : 'Error', {
        description: errorMessage
      });
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 w-full flex items-center justify-center bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700">
        <Loader2 className="w-16 h-16 animate-spin text-white" />
      </div>
    );
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  return (
    <div className="flex-1 w-full flex items-center justify-center bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 p-6">
      <Card className="max-w-md w-full p-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-950 flex items-center justify-center">
              <Lock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <h1 className="text-3xl font-bold">
            {dashboardType === 'consultant' ? 'Consultant' : 'SDR'} Dashboard
          </h1>
          <p className="text-muted-foreground">
            This dashboard requires access verification
          </p>
        </div>

        {!codeRequested ? (
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg space-y-2">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900 dark:text-blue-100">
                  <p className="font-medium mb-1">How to access:</p>
                  <ol className="list-decimal list-inside space-y-1 text-blue-800 dark:text-blue-200">
                    <li>Request an access code</li>
                    <li>Log into the CRM as lcosta or njunior</li>
                    <li>Check the bell icon 🔔 for your code</li>
                    <li>Enter the code here within 15 minutes</li>
                  </ol>
                </div>
              </div>
            </div>

            {lastError && lastError.isRetryable && (
              <div className="bg-yellow-50 dark:bg-yellow-950/30 p-4 rounded-lg space-y-2">
                <div className="flex items-start gap-2">
                  <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-yellow-900 dark:text-yellow-100 mb-1">
                      Connection Issue
                    </p>
                    <p className="text-yellow-800 dark:text-yellow-200">
                      {lastError.message}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={handleRequestCode}
              disabled={requesting}
              className="w-full"
              size="lg"
            >
              {requesting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Requesting Code...
                </>
              ) : lastError?.isRetryable ? (
                <>
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  Retry Request Code
                </>
              ) : (
                'Request Access Code'
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-950/30 p-4 rounded-lg">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-green-900 dark:text-green-100 mb-1">
                    Code Generated Successfully
                  </p>
                  <p className="text-green-800 dark:text-green-200">
                    Check the CRM notifications (bell icon 🔔) to see your access code.
                    Code expires in 15 minutes.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Enter Access Code</label>
              <Input
                type="text"
                placeholder="000000"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-widest font-mono"
              />
            </div>

            {lastError && lastError.isRetryable && (
              <div className="bg-yellow-50 dark:bg-yellow-950/30 p-4 rounded-lg space-y-2">
                <div className="flex items-start gap-2">
                  <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-yellow-900 dark:text-yellow-100 mb-1">
                      Connection Issue
                    </p>
                    <p className="text-yellow-800 dark:text-yellow-200">
                      {lastError.message}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Button
                onClick={handleValidateCode}
                disabled={validating || code.length !== 6}
                className="w-full"
                size="lg"
              >
                {validating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Validating...
                  </>
                ) : (
                  'Validate Code'
                )}
              </Button>

              <Button
                onClick={() => {
                  setCodeRequested(false);
                  setCode('');
                  setLastError(null);
                }}
                variant="ghost"
                className="w-full"
              >
                Request New Code
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};
